import React, { useState } from 'react';
import PatientAppointmentSummary from './PatientAppointmentSummary';
import PatientVisitInformation from './PatientVisitInformation';
import PaymentHistory from './PaymentHistory';
import { FaArrowLeft, FaUser, FaSearchPlus } from 'react-icons/fa';
import { Stethoscope, X, Check, Printer, Tag } from 'lucide-react';
import './PatientInfoPanel.css';
import './AddPackage.css';
import AddPackage from './AddPackage';
import { toast } from 'react-toastify';
import axios from 'axios';
import ENDPOINTS, { API_BASE_URL } from '../../../Constants/ApiEndpoints';
import PatientStickerLabel from '../../../CommonComponents/Utils/PatientStickerLabel';
import { dateTimeFormatter } from '../../../CommonComponents/Utils/DateTimeFormatter';

const PatientInfoPanel = ({ patient, onBack }) => {

  const [isPackagePopupOpen, setIsPackagePopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAdmitModal, setShowAdmitModal] = useState(false);
  const [isEmergency, setIsEmergency] = useState(false);
  const [admitLoading, setAdmitLoading] = useState(false);

  const [isFlagged, setIsFlagged] = useState(patient.flagged);
  const [packageRefreshKey, setPackageRefreshKey] = useState(0);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);

  const handleAdmitSubmit = async () => {
    setAdmitLoading(true);
    const todayDate = new Date().toISOString().split('T')[0];
    const currentTime = new Date().toTimeString().split(' ')[0];

    try {
      await axios.post(`${API_BASE_URL}/admitPatient`, {
        mrNumber: patient.mrNumber,
        doctorId: patient.doctorId || 1,
        admissionDate: todayDate,
        admissionTime: currentTime,
        patientType: 'IN_PATIENT',
        emergency: isEmergency,
        admissionStatus: 'PENDING',
        roomType: null,
        bedNumber: null,
      });
      toast.success("Patient admitted successfully! Room and bed can be configured in Inpatient module.");
      setShowAdmitModal(false);
    } catch (err) {
      console.error("Admit patient failed:", err);
      toast.error("Failed to admit patient.");
    } finally {
      setAdmitLoading(false);
    }
  };

  const handleFlagClick = async () => {
    const newFlagValue = !isFlagged;
    setLoading(true);
    try {
      await axios.patch(
        `${API_BASE_URL}/flagThePatient/${patient.mrNumber}/${newFlagValue}`,
      );
      setIsFlagged(newFlagValue);
      toast.success("Patient flag status updated!");
    } catch (err) {
      console.error("Failed to update flag:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="patient-info-wrapper">
      {/* Top Header Navigation */}
      <div className="breadcrumbb">
        <div className="breadcrumbb-title">
          Dashboard / <strong>Appointments/Appointment</strong>
        </div>
        <button onClick={onBack} className="custom-back-button">
          <FaArrowLeft className="icon-left" />
          Back
        </button>
      </div>

      {/* Enterprise Unified Patient Hero Card */}
      <div className="patient-hero-card">
        <div className="patient-card-accent-bar" />

        {/* Hero Profile Header: Circular Photo Avatar + Patient Info & Badges */}
        <div className="patient-hero-header">
          <div
            className="patient-avatar-wrapper clickable-avatar"
            onClick={() => setShowImagePreview(true)}
            title="Click to view full picture"
          >
            {patient.photo ? (
              <img
                src={`data:image/jpeg;base64,${patient.photo}`}
                alt="Patient"
                className="patient-hero-avatar"
                loading="lazy"
              />
            ) : (
              <div className="patient-avatar-placeholder">
                <FaUser size={40} color="#94a3b8" />
              </div>
            )}
            <div className="avatar-hover-overlay">
              <FaSearchPlus size={20} color="#ffffff" />
            </div>
          </div>

          <div className="patient-identity-block">
            <div className="patient-title-row">
              <h2 className="patient-hero-name">{patient.patientName || 'Patient Name'}</h2>
              {patient.gender && <span className="hero-meta-badge">{patient.gender}</span>}
              {patient.age && <span className="hero-meta-badge">{patient.age} yrs</span>}
            </div>

            <div className="patient-sub-badges">
              <button
                className="priority-flag-btn"
                onClick={handleFlagClick}
                disabled={loading}
                style={{
                  backgroundColor: isFlagged ? "#dc2626" : "#16a34a",
                }}
              >
                {loading ? "Processing..." : isFlagged ? "🚨 Flagged" : "🚨 Flag"}
              </button>
            </div>
          </div>

          <div className="hero-right-actions">
            <button className="print-label-btn" onClick={() => setShowLabelModal(true)} title="Print Sticker Label for Patient File">
              <Tag size={14} />
              <span>Label</span>
            </button>
            <span className="token-badge">Token #{patient.tokenNumber}</span>
          </div>
        </div>

        {/* 2-Column Grid: Left (Appointment Summary) & Right (Visit Info) */}
        <div className="patient-details-body">
          <div className="details-card-left">
            <h4 className="card-section-title">📅 Appointment Summary</h4>
            <PatientAppointmentSummary patient={patient} />
          </div>

          <div className="details-card-right">
            <h4 className="card-section-title">📋 Visit & Booking Information</h4>
            <PatientVisitInformation patient={patient} />
          </div>
        </div>

        {/* Card Action Footer */}
        <div className="patient-card-footer">
          <button className="edit-appointment-btn" onClick={() => setIsPackagePopupOpen(true)}>
            ➕ Add Package
          </button>
          <button className="view-history-btn" onClick={() => setShowAdmitModal(true)}>
            🏥 Admit Patient
          </button>
        </div>
      </div>

      {/* Large High-Res Image Preview Modal (Identical to InfoDisplay) */}
      {showImagePreview && (
        <div className="photo-lightbox-backdrop" onClick={() => setShowImagePreview(false)}>
          <div className="photo-lightbox-modal" onClick={(e) => e.stopPropagation()}>
            <button className="photo-lightbox-close" onClick={() => setShowImagePreview(false)}>&times;</button>
            <img
              src={patient.photo ? `data:image/jpeg;base64,${patient.photo}` : "/VThospitalsIcon.png"}
              alt={patient.patientName}
              className="photo-lightbox-img"
            />
            <div className="photo-lightbox-caption">
              <strong>{patient.patientName}</strong> (MR: {patient.mrNumber || 'N/A'})
            </div>
          </div>
        </div>
      )}

      {/* Add Package Modal */}
      <AddPackage
        isOpen={isPackagePopupOpen}
        onClose={() => setIsPackagePopupOpen(false)}
        appointmentId={patient.appointmentId}
        admissionId={patient.admissionId}
        onSuccess={() => {
          toast.success('Package added successfully!');
          setIsPackagePopupOpen(false);
          setPackageRefreshKey(k => k + 1);
        }}
      />

      {/* Admit Patient Modal */}
      {showAdmitModal && (
        <div className="modern-modal-overlay" onClick={() => setShowAdmitModal(false)}>
          <div className="modern-modal-card animated-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-top-accent-bar indigo-accent" />
            <div className="modern-modal-header">
              <div className="modal-title-container">
                <div className="modal-icon-badge indigo-glow">
                  <Stethoscope size={20} className="icon-indigo" />
                </div>
                <div>
                  <h3 className="modal-main-heading">Admit Patient to Inpatient</h3>
                  <p className="modal-sub-heading">Initiate IP admission and create inpatient record</p>
                </div>
              </div>
              <button className="modal-close-icon-btn" onClick={() => setShowAdmitModal(false)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="modern-modal-body">
              <div className="admit-info-banner">
                <p className="confirm-modal-msg">
                  Are you sure you want to admit <strong>{patient.patientName}</strong> (MR: {patient.mrNumber})?
                </p>
                <p className="confirm-modal-subtext">
                  An Inpatient (IP) record will be created. Room and bed assignment details can be filled in the Inpatient module.
                </p>
              </div>

              <label className="checkbox-card-group">
                <input
                  type="checkbox"
                  checked={isEmergency}
                  onChange={(e) => setIsEmergency(e.target.checked)}
                />
                <div>
                  <span className="checkbox-title">Emergency Admission</span>
                  <span className="checkbox-desc">Flag this IP admission with high priority status</span>
                </div>
              </label>
            </div>
            <div className="modern-modal-footer">
              <button className="modern-btn-secondary" onClick={() => setShowAdmitModal(false)} disabled={admitLoading}>
                Cancel
              </button>
              <button className="modern-btn-primary" onClick={handleAdmitSubmit} disabled={admitLoading}>
                <Check size={16} />
                <span>{admitLoading ? 'Admitting...' : 'Confirm Admission'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reusable Patient File Sticker Label Modal */}
      <PatientStickerLabel
        isOpen={showLabelModal}
        onClose={() => setShowLabelModal(false)}
        patient={patient}
      />

      {/* Payment History Component */}
      <div className="payment-history">
        <PaymentHistory patient={patient} refreshKey={packageRefreshKey} />
      </div>
    </div>
  );
};

export default PatientInfoPanel;
